import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Login } from "./Pages/Login";
import { Register } from "./Pages/Register";
import { AdminRoutes, EstablishmentRoutes, rotaInicialDaSessao } from "./Components/Auth/isAuthenticated";
import { ToastContainer } from "react-toastify";

// ── Menus ──────────────────────────────────────────────────────────────────────
import PageHeaderUser          from "./Pages/Menu";
import PageHeaderEstablishment from "./Pages/MenuEstablishment";

// ── Admin pages ────────────────────────────────────────────────────────────────
import { Overview }                     from "./Pages/Admin/Overview";
import { UserManagement }               from "./Pages/Admin/UserManagement";
import { PlatformReport }               from "./Pages/Admin/PlatformReport";
import { UserAdminRegister }            from "./Pages/Admin/UserAdminRegister";
import { EstablishmentsRegistration }   from "./Pages/Admin/EstablishmentsRegistration";
import { UserDetails }                  from "./Pages/Admin/DetailsUser";
import { EstablishmentDetails }         from "./Pages/Admin/DetailsEstablishment";
import { Banner }                       from "./Pages/Admin/Banner";
import { BannerDetails }                from "./Pages/Admin/Banner/BannerDetails";
import { Event }                        from "./Pages/Admin/Eventos";
import { EventDetails }                 from "./Pages/Admin/Eventos/EventDetails";
import { Speaker }                      from "./Pages/Admin/Speaker";
import { SpeakerDetails }               from "./Pages/Admin/Speaker/SpeakerDetails";
import { InstitutionalStories }         from "./Pages/Admin/Story/InstitutionalStories";
import { EstabilishmentUserManagement } from "./Pages/Estabilishment/EstabilishmentUserManagement";

// ── Establishment pages ────────────────────────────────────────────────────────
import { HomeEstablishment }     from "./Pages/Estabilishment/HomeEstablishment";
import { MyEstablishments }      from "./Pages/Estabilishment/MyEstablishments";
import { Subscription }          from "./Pages/Estabilishment/Subscription";
import { EstablishmentProfile }  from "./Pages/Estabilishment/EstablishmentProfile";
import { Support }               from "./Pages/Estabilishment/Support";
import { Notification }          from "./Pages/Estabilishment/Notification";
import { Banners }               from "./Pages/Estabilishment/Banners";
import { BannerForm }            from "./Pages/Estabilishment/BannerForm";
import { DetailsEstabilishmentUser } from "./Pages/Admin/DetailsEstabilishmentUser";
import { StripeSuccess }         from "./Pages/Stripe/StripeSuccess";
import { StripeCancel }          from "./Pages/Stripe/StripeCancel";
import { Analytics } from "./Components/Analytics";

/**
 * Entrada pública: "/", "/login" e qualquer caminho desconhecido.
 *
 * Com sessão válida, manda para o painel do perfil em vez de desenhar o login.
 * As três rotas desenhavam <Login /> sem olhar a sessão, e é isso que fazia o
 * "voltar" do navegador cair na tela de login mesmo logado — o histórico
 * guarda uma entrada de /login, vinda do window.location.href que o Logout
 * dispara, e voltar para ela reabria o formulário.
 *
 * Sem perfil para este painel, rotaInicialDaSessao devolve null e o login é
 * desenhado normalmente: é lá que o usuário recebe a mensagem explicando.
 */
const EntradaPublica = () => {
  const destino = rotaInicialDaSessao();

  return destino ? <Navigate to={destino} replace /> : <Login />;
};

function Router() {
  return (
    <BrowserRouter>
    <Analytics />
      <Routes>
        {/* ── Public ──────────────────────────────────────────────────────── */}
        <Route path="/login"    element={<EntradaPublica />} />
        <Route path="/register" element={<Register />} />
        <Route path="/stripe/success" element={<StripeSuccess />} />
        <Route path="/stripe/cancel" element={<StripeCancel />} />
        <Route path="/"         element={<EntradaPublica />} />

        {/* ── Admin routes ────────────────────────────────────────────────── */}
        <Route path="/" element={<AdminRoutes />}>
          <Route path="/home"
            element={<PageHeaderUser InternalComponent={<Overview />} />}
          />
          <Route path="/banner"
            element={<PageHeaderUser InternalComponent={<Banner />} />}
          />
          <Route path="/banner/:bannerId"
            element={<PageHeaderUser InternalComponent={<BannerDetails />} />}
          />
          <Route path="/momentos-institucionais"
            element={<PageHeaderUser InternalComponent={<InstitutionalStories />} />}
          />
          <Route path="/eventos"
            element={<PageHeaderUser InternalComponent={<Event />} />}
          />
          <Route path="/eventos/:eventId"
            element={<PageHeaderUser InternalComponent={<EventDetails />} />}
          />
          <Route path="/speaker"
            element={<PageHeaderUser InternalComponent={<Speaker />} />}
          />
          <Route path="/speaker/:speakerId"
            element={<PageHeaderUser InternalComponent={<SpeakerDetails />} />}
          />
          <Route path="/UserManagement"
            element={<PageHeaderUser InternalComponent={<UserManagement />} />}
          />
          <Route path="/UserManagement/:userId"
            element={<PageHeaderUser InternalComponent={<UserDetails />} />}
          />
          <Route path="/UserAdminRegister"
            element={<PageHeaderUser InternalComponent={<UserAdminRegister />} />}
          />
          <Route path="/UserAdminRegister/:userId"
            element={<PageHeaderUser InternalComponent={<UserDetails />} />}
          />
          <Route path="/EstablishmentsRegistration"
            element={<PageHeaderUser InternalComponent={<EstablishmentsRegistration />} />}
          />
          <Route path="/EstablishmentsRegistration/:establishmentID"
            element={<PageHeaderUser InternalComponent={<EstablishmentDetails />} />}
          />
          <Route path="/PlatformReport"
            element={<PageHeaderUser InternalComponent={<PlatformReport />} />}
          />
          <Route path="/EstabilishmentUserManagement"
            element={<PageHeaderUser InternalComponent={<EstabilishmentUserManagement />} />}
          />
          <Route path="/EstabilishmentUserManagement/:userId"
            element={<PageHeaderUser InternalComponent={<DetailsEstabilishmentUser />} />}
          />
        </Route>

        {/* ── Establishment routes ─────────────────────────────────────────── */}
        <Route path="/" element={<EstablishmentRoutes />}>
          <Route path="/home_establishment"
            element={<PageHeaderEstablishment InternalComponent={<HomeEstablishment />} />}
          />
          <Route path="/establishment/establishments"
            element={<PageHeaderEstablishment InternalComponent={<MyEstablishments />} />}
          />
          <Route path="/establishment/notification"
            element={<PageHeaderEstablishment InternalComponent={<Notification />} />}
          />
          <Route path="/establishment/subscription"
            element={<PageHeaderEstablishment InternalComponent={<Subscription />} />}
          />
          <Route path="/establishment/profile"
            element={<PageHeaderEstablishment InternalComponent={<EstablishmentProfile />} />}
          />
          <Route path="/establishment/support"
            element={<PageHeaderEstablishment InternalComponent={<Support />} />}
          />
          <Route path="/establishment/banners"
            element={<PageHeaderEstablishment InternalComponent={<Banners />} />}
          />
          <Route path="/establishment/banners/create"
            element={<PageHeaderEstablishment InternalComponent={<BannerForm />} />}
          />
          <Route path="/establishment/banners/:id/edit"
            element={<PageHeaderEstablishment InternalComponent={<BannerForm />} />}
          />
        </Route>

        {/* ── Fallback ────────────────────────────────────────────────────── */}
        <Route path="*" element={<EntradaPublica />} />
      </Routes>
      <ToastContainer />
    </BrowserRouter>
  );
}

export default Router;